import { HttpStatusCode } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  GetUserSubscriptionDetailsDocument,
  UserSubscriptionDetails,
  UserSubscriptionDetailsResult,
} from '@shared/graphql/account.generated';
import {
  GetDidQueryService,
  LogoutMutationService,
  RefreshTokenMutationService,
} from '@shared/graphql/auth.generated';
import { GraphqlClients, GraphqlService } from '@shared/graphql/graphql.service';
import { IAccessTokenPayload } from '@shared/models/access-token.interface';
import { jwtDecode } from 'jwt-decode';
import { catchError, map, of, take, tap } from 'rxjs';
import { variables } from '../helper/configs';
import { GeneralService } from './general.service';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  userInfo = signal<IAccessTokenPayload | undefined>(undefined);
  userSubscriptionDetails = signal<undefined | UserSubscriptionDetails>(undefined);

  private readonly refreshTokenMutationService = inject(RefreshTokenMutationService);
  private readonly logoutMutationService = inject(LogoutMutationService);
  private readonly getDidQueryService = inject(GetDidQueryService);
  private readonly generalService = inject(GeneralService);
  private readonly graphqlService = inject(GraphqlService);

  constructor() {
    const atk = this.getAtk();
    if (atk) {
      try {
        const token: any = jwtDecode(atk);
        const user: IAccessTokenPayload = {
          id: token.sub,
          did: token.did,
          email: token.e,
          roles: token.rs,
          mobile: token.m,
          activeProfile: token.ap,
          otherProfiles: token.op,
          activeSubscription: token.asp,
        };
        this.userInfo.set(user);
      } catch (error) {
        this.userInfo.set(undefined);
      }
    }
  }

  getAtk(): string | null {
    if (this.generalService.isBrowserOnly) {
      return localStorage.getItem(variables.accessToken);
    }
    return null;
  }

  getRtk(): string | null {
    if (this.generalService.isBrowserOnly) {
      return localStorage.getItem(variables.refreshToken);
    }
    return null;
  }

  getTmt(): string | null {
    if (this.generalService.isBrowserOnly) {
      return localStorage.getItem(variables.tmtToken);
    }
    return null;
  }

  setUserInfo(atk: string, rtk: string) {
    localStorage.setItem(variables.accessToken, atk);
    localStorage.setItem(variables.refreshToken, rtk);
    const token: any = jwtDecode(atk);
    const user: IAccessTokenPayload = {
      id: token.sub,
      did: token.did,
      email: token.e,
      roles: token.rs,
      mobile: token.m,
      activeProfile: token.ap,
      otherProfiles: token.op,
      activeSubscription: token.asp,
    };
    localStorage.setItem(variables.ga4UserId, user.id);
    this.userInfo.set(user);
  }

  refreshToken() {
    this.refreshTokenMutationService.client = GraphqlClients.auth;
    return this.refreshTokenMutationService.mutate().pipe(
      tap((res) => {
        this.setUserInfo(res.data?.refreshToken?.data?.atk!, res.data?.refreshToken?.data?.rtk!);
        return res;
      }),
      catchError((e) => {
        if (e.networkError.statusCode === HttpStatusCode.UnprocessableEntity) {
          this.logout().subscribe({
            next: () => {
              if (this.generalService.isBrowserOnly) {
                localStorage.removeItem(variables.did);
              }
            },
          });
        } else if (e.networkError.statusCode === HttpStatusCode.Unauthorized) {
          this.logout().subscribe();
        }
        throw e;
      }),
    );
  }

  logout(returnUrl?: string) {
    this.logoutMutationService.client = GraphqlClients.auth;
    return this.logoutMutationService.mutate().pipe(
      take(1),
      map((res) => {
        this.logoutFunction(returnUrl);
        return res;
      }),
      catchError((e) => {
        this.logoutFunction(returnUrl);
        throw e;
      }),
    );
  }

  private logoutFunction(returnUrl?: string) {
    if (this.generalService.isBrowserOnly) {
      localStorage.removeItem(variables.accessToken);
      localStorage.removeItem(variables.refreshToken);
      localStorage.removeItem(variables.ga4UserId);
      this.userSubscriptionDetails.set(undefined);
      this.userInfo.set(undefined);
      this.generalService.router.navigate(['preparation'], {
        queryParams: {
          ru: returnUrl,
        },
      });
    }
  }

  getDeviceId() {
    this.getDidQueryService.client = GraphqlClients.auth;
    return this.getDidQueryService.watch().valueChanges.pipe(
      map((res) => {
        localStorage.setItem(variables.did, res.data.getDid?.data?.did!);
        return res;
      }),
    );
  }

  getUserSubscriptionDetails() {
    if (this.generalService.isBrowserOnly && this.getRtk()) {
      return this.graphqlService
        .makeQuery<UserSubscriptionDetailsResult>(
          GraphqlClients.account,
          GetUserSubscriptionDetailsDocument,
        )
        .pipe(
          map((res) => {
            this.userSubscriptionDetails.set(res.data!);
            return res;
          }),
        );
    }
    return of();
  }
}
